import React from 'react';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../components/ui/Table';
import { PiTrash, PiCheck, PiCopySimple } from 'react-icons/pi';

export function Overview({
    currentUser,
    statusInfo,
    planLabel,
    hasSeatPlan,
    activeSeatCount,
    seatsAllowed,
    seatsRemaining,
    pendingInviteCount,
    members = [],
    invites = [],
    hasActiveSub,
    stripeData,
    inviteEmail,
    setInviteEmail,
    handleCreateInvite,
    inviteLoading,
    deleteInviteLoadingCode,
    handleDeleteInvite,
    handleRemoveMember,
    removeMemberLoadingUid,
    copyToClipboard,
    copiedCode,
    workspaceMetrics
}) {
    return (
        <section className="dash-stack">

            {/* Top Metric Cards */}
            <div className="dash-grid text-left" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                <article className="dash-card metric-card">
                    <p>Last 30 Days</p>
                    <h3>{workspaceMetrics?.callsLast30Days || 0}</h3>
                    <span>Calls Analyzed</span>
                </article>

                <article className="dash-card metric-card">
                    <p>Workspace Capacity</p>
                    <h3>{hasSeatPlan ? `${activeSeatCount}/${seatsAllowed}` : activeSeatCount}</h3>
                    <span>{planLabel ? `${planLabel} Plan` : 'Free Plan'} Seats Active</span>
                </article>
            </div>

            {/* Bottom Section: Members & Invites */}
            <div className="dash-grid" style={{ gridTemplateColumns: '2fr 1fr', marginTop: 16 }}>

                {/* Left: Team Members */}
                <article className="dash-card" style={{ padding: 0, overflow: 'hidden' }}>
                    <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border-color)' }}>
                        <h4 style={{ margin: 0, fontSize: 16 }}>Members ({members.length})</h4>
                    </div>

                    {members.length === 0 ? (
                        <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>No active members.</div>
                    ) : (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableHeader>User</TableHeader>
                                    <TableHeader>Role</TableHeader>
                                    <TableHeader>Calls</TableHeader>
                                    <TableHeader className="text-right">Action</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {members.map((member) => {
                                    const memberEmail = member.email || '';
                                    const isCurrentUser = member.uid === currentUser?.uid;
                                    const actionBusy = removeMemberLoadingUid === member.uid;

                                    return (
                                        <TableRow key={member.uid}>
                                            <TableCell>
                                                <span style={{ fontWeight: 500 }}>{memberEmail.split('@')[0]}</span><br />
                                                <span className="text-muted" style={{ fontSize: 13 }}>{memberEmail}</span>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={member.role === 'admin' ? 'blue' : 'neutral'}>
                                                    {member.role || 'user'}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-muted" style={{ fontWeight: 500 }}>
                                                {workspaceMetrics?.userCallCounts?.[member.uid] || 0}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {isCurrentUser ? null : (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleRemoveMember(member)}
                                                        loading={actionBusy}
                                                        style={{ color: '#dc2626', padding: '6px' }}
                                                    >
                                                        <PiTrash size={20} />
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </article>

                {/* Right: Invites Pane */}
                <article className="dash-card" style={{ padding: 0, overflow: 'hidden', height: 'fit-content' }}>
                    <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-slate)' }}>
                        <h4 style={{ margin: '0 0 12px', fontSize: 16 }}>Invites</h4>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <input
                                type="email"
                                placeholder="enter invite email"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                className="ui-input"
                                style={{ flex: 1 }}
                            />
                            <Button
                                variant="primary"
                                onClick={handleCreateInvite}
                                disabled={!inviteEmail.trim() || !hasActiveSub}
                                loading={inviteLoading}
                            >
                                Send
                            </Button>
                        </div>
                        {!hasActiveSub && stripeData && (
                            <p className="inline-error" style={{ margin: '8px 0 0', fontSize: 12, padding: '8px' }}>
                                Subscription inactive.
                            </p>
                        )}
                    </div>

                    {invites.length > 0 && (
                        <div>
                            <div style={{ padding: '12px 24px', background: 'var(--bg-secondary)', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)' }}>
                                Pending Invites
                            </div>
                            {invites.map((invite) => {
                                const isDeleting = deleteInviteLoadingCode === invite.code;
                                const copied = copiedCode === invite.code;

                                return (
                                    <div key={invite.code} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 24px', borderBottom: '1px solid var(--border-color)' }}>
                                        <div style={{ minWidth: 0, paddingRight: 12 }}>
                                            <div style={{ fontWeight: 500, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {invite.allowedEmail || 'Open invite'}
                                            </div>
                                            <code style={{ fontSize: 11, color: 'var(--text-muted)', wordBreak: 'break-all' }}>
                                                {`/rep/join/${invite.code}`}
                                            </code>
                                        </div>
                                        <div style={{ display: 'flex', gap: 4 }}>
                                            <Button variant="ghost" size="sm" onClick={() => {
                                                const baseUrl = window.location.origin;
                                                copyToClipboard(`${baseUrl}/rep/join/${invite.code}`);
                                            }} style={{ padding: 6 }}>
                                                {copied ? <PiCheck size={18} /> : <PiCopySimple size={18} />}
                                            </Button>
                                            <Button variant="ghost" size="sm" onClick={() => handleDeleteInvite(invite.code)} loading={isDeleting} style={{ padding: 6, color: '#dc2626' }}>
                                                <PiTrash size={18} />
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </article>
            </div>
        </section>
    );
}
